import { axios_tenant } from "../../../../axios";
import { downloadBlobResponse, XLSX_CONTENT_TYPE } from "../utils/downloadHelpers";

/**
 * Marks a download failure that the report screen is responsible for telling the user about.
 *
 * The axios response interceptor (src/axios.js) already toasts anything that came back with an
 * HTTP status, so reporting those again would show the same message twice. What it stays quiet
 * about is tagged here instead: a request that never reached the server, and a 200 whose body
 * turned out to be a JSON error rather than a workbook.
 *
 * Tagged via `name` rather than an Error subclass, because `instanceof` stops being reliable
 * once the class is transpiled down.
 */
const REPORT_EXPORT_ERROR = "ReportExportError";

const reportExportError = (cause) => {
  const error = new Error(cause?.message || "Failed to download the report.");
  error.name = REPORT_EXPORT_ERROR;
  return error;
};

/** True for the failures a caller should surface itself. See `reportExportError`. */
export const isReportExportError = (error) => error?.name === REPORT_EXPORT_ERROR;

/**
 * Downloads a report as xlsx.
 *
 * The same endpoint that feeds the grid regenerates the workbook, so `filters` must be the
 * object the list request already used - that is what keeps the sheet to the filtered set
 * across every page rather than just the rows on screen.
 *
 * `exportFields` is the column picker's current selection; the server narrows the sheet to it.
 */
export async function downloadReportXlsx(endpoint, filters, exportFields, filename) {
  let response;

  try {
    response = await axios_tenant.get(endpoint, {
      params: { ...filters, format: "xlsx", columns: exportFields.join(",") },
      responseType: "blob",
    });
  } catch (error) {
    // Anything carrying a response has been toasted already; a network failure has not.
    if (error?.response) throw error;
    throw reportExportError(error);
  }

  try {
    await downloadBlobResponse(response, filename, XLSX_CONTENT_TYPE);
  } catch (error) {
    throw reportExportError(error);
  }
}
